import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Purchase options (one-time + selling plans) for the product form.
 * Associates a `selling_plan` input with the buy-buttons form via the HTML form attribute.
 *
 * @typedef {object} PurchaseOptionsRefs
 * @property {HTMLInputElement} sellingPlanInput
 * @extends Component<PurchaseOptionsRefs>
 */
class PurchaseOptions extends Component {
  requiredRefs = ['sellingPlanInput'];

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#onOptionChange, { signal });

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.#syncFromCheckedOption();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * @param {Event} event
   */
  #onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    this.#applySelectedOption(target);
  };

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        if (detail.newProduct) {
          this.dataset.productId = String(detail.newProduct.id);
        } else if (detail.productId && String(detail.productId) !== this.dataset.productId) {
          return;
        }

        const variantId = detail.resource?.id;
        if (variantId == null) return;

        this.#showVariantFieldset(String(variantId));
        this.#syncFromCheckedOption();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[purchase-options] Event promise rejected:', error);
        }
      });
  };

  /**
   * @param {string} variantId
   */
  #showVariantFieldset(variantId) {
    const fieldsets = this.querySelectorAll('fieldset[data-variant-id]');
    fieldsets.forEach((fieldset) => {
      const isMatch = fieldset.getAttribute('data-variant-id') === variantId;
      fieldset.hidden = !isMatch;
      fieldset.classList.toggle('purchase-options__fieldset--hidden', !isMatch);
    });
  }

  #syncFromCheckedOption() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset:not([hidden]) input[type="radio"]:checked')
    );

    if (checked) {
      this.#applySelectedOption(checked);
      return;
    }

    const firstEnabled = /** @type {HTMLInputElement | null} */ (
      this.querySelector('fieldset:not([hidden]) input[type="radio"]:not(:disabled)')
    );

    if (firstEnabled) {
      firstEnabled.checked = true;
      this.#applySelectedOption(firstEnabled);
      return;
    }

    this.refs.sellingPlanInput.value = '';
  }

  /**
   * @param {HTMLInputElement} input
   */
  #applySelectedOption(input) {
    this.refs.sellingPlanInput.value = input.dataset.sellingPlanId || '';
  }
}

if (!customElements.get('purchase-options')) {
  customElements.define('purchase-options', PurchaseOptions);
}
